import { useState } from 'react';
import type { BarChartItem } from '../types';

interface BarChartProps {
	bars: BarChartItem[];
}

function fmtNum(n: number): string {
	if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
	if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
	return n.toLocaleString();
}

export function BarChart({ bars }: BarChartProps) {
	const [tip, setTip] = useState<BarChartItem | null>(null);
	const maxVal = Math.max(...bars.map((d) => d.total), 1) * 1.15;

	return (
		<div>
			<div className="mb-2 flex items-center gap-4 text-xs text-muted-foreground">
				<span className="flex items-center gap-1.5">
					<span className="inline-block h-2.5 w-2.5 rounded-sm bg-green-500" />
					Successful
				</span>
				<span className="flex items-center gap-1.5">
					<span className="inline-block h-2.5 w-2.5 rounded-sm bg-red-400" />
					Failed
				</span>
			</div>

			{tip && (
				<div className="mb-2 rounded border bg-muted/50 p-2 text-xs">
					<p className="font-medium">{tip.label}</p>
					<p>
						Total {tip.total.toLocaleString()} · Success{' '}
						{tip.success.toLocaleString()} (
						{Math.round((tip.success / tip.total) * 100)}%) · Failed{' '}
						{tip.failed.toLocaleString()} (
						{Math.round((tip.failed / tip.total) * 100)}%)
					</p>
				</div>
			)}

			<div className="mb-1 flex justify-between text-[10px] text-muted-foreground">
				{[0, 0.25, 0.5, 0.75, 1].map((f) => (
					<span key={f}>{fmtNum(Math.round(f * maxVal))}</span>
				))}
			</div>

			<div className="space-y-1.5">
				{bars.map((d) => {
					const tw = (d.total / maxVal) * 100;
					const sw = d.total > 0 ? (d.success / d.total) * tw : 0;
					const fw = d.total > 0 ? (d.failed / d.total) * tw : 0;

					return (
						<div
							key={d.label}
							className="flex items-center gap-2 text-xs"
							role="button"
							tabIndex={0}
							onMouseEnter={() => setTip(d)}
							onMouseLeave={() => setTip(null)}
						>
							<span className="w-20 shrink-0 text-right text-muted-foreground">
								{d.label}
							</span>
							<div className="relative h-5 flex-1">
								{sw > 0 && (
									<div
										className="absolute inset-y-0 left-0 rounded-sm bg-green-500"
										style={{ width: `${sw}%` }}
									/>
								)}
								{fw > 0 && (
									<div
										className="absolute inset-y-0 rounded-sm bg-red-400"
										style={{ left: `${sw}%`, width: `${fw}%` }}
									/>
								)}
								{tw > 18 && (
									<span className="absolute inset-y-0 left-0 flex items-center pl-1 text-[10px] text-white">
										{fmtNum(d.total)}
									</span>
								)}
							</div>
							<span className="w-14 text-right">{fmtNum(d.total)}</span>
						</div>
					);
				})}
			</div>
		</div>
	);
}
